import type { ZeytalCLIPureConfig } from "@zeytal/kit";
import type { FigmaRestApi, ZeytalTokens } from "@zeytal/types";

import { toKebabCase } from "@endgame/utils/toKebabCase";

import { colorMaker } from "../core/makers/colors/index.ts";
import { effectMaker } from "../core/makers/effects/index.ts";
import { fontMaker } from "../core/makers/fonts/index.ts";
import { gridMaker } from "../core/makers/grids/index.ts";

export const transformStylesValues = (
  styles: ZeytalTokens.StructuredStylesArray,
  userConfig: ZeytalCLIPureConfig,
) => {
  const transformedStyles = styles.reduce((acc, { name, styleType, value }) => {
    let tempTransformedStyles: ZeytalTokens.TransformedTokensArray = [];

    if (styleType === "FILL") {
      const styleVal = colorMaker(
        userConfig.styles.formats,
        value as FigmaRestApi.Paint[],
      );

      tempTransformedStyles = [
        { name, value: styleVal, originType: styleType },
      ];
    } else if (styleType === "EFFECT") {
      const styleVal = effectMaker(
        userConfig.styles.formats,
        value as FigmaRestApi.Effect[],
      );

      tempTransformedStyles = Object.entries(styleVal).map(([key, val]) => ({
        name: `${name}-${toKebabCase(
          key.replace(/[A-Z]+/g, (word) => " " + word), // Add a space after capital letters
        )}`,
        value: Array.isArray(val) ? val.join(", ") : val,
        originType: styleType,
      }));
    } else if (styleType === "TEXT") {
      const styleVal = fontMaker(
        userConfig.styles.formats,
        value as FigmaRestApi.TypeStyle,
      );

      tempTransformedStyles = Object.entries(styleVal).map(([key, val]) => ({
        name: `${name}-${toKebabCase(
          key.replace(/[A-Z]+/g, (word) => " " + word), // Add a space after capital letters
        )}`,
        value: val,
        originType: styleType,
      }));
    } else if (styleType === "GRID") {
      const styleVal = gridMaker(
        userConfig.styles.formats,
        value as FigmaRestApi.LayoutGrid[],
      );

      tempTransformedStyles = Object.entries(styleVal).reduce(
        (gridAcc, [gridKey, gridVal]) => {
          Object.entries(gridVal).forEach(([k, v]) => {
            gridAcc.push({
              name: `${name}-${toKebabCase(
                gridKey.replace(/[A-Z]+/g, (word) => " " + word), // Add a space after capital letters
              )}-${toKebabCase(
                k.replace(/[A-Z]+/g, (word) => " " + word), // Add a space after capital letters
              )}`,
              value: v,
              originType: styleType,
            });
          });

          return gridAcc;
        },
        [] as ZeytalTokens.TransformedTokensArray,
      );
    }

    acc.push(...tempTransformedStyles);

    return acc;
  }, [] as ZeytalTokens.TransformedTokensArray);

  return transformedStyles;
};
